import React, { useEffect, useState } from "react";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { FaCheck, FaTimes, FaTrash, FaEye } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from "../App";

function ManageUsers() {

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMobile, setFilterMobile] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [reason, setReason] = useState("");
  const [loadingApprove, setLoadingApprove] = useState(null);
  const [loadingDisapprove, setLoadingDisapprove] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(null);

  
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(
        `${baseUrl}/users`
      );
      const sortedUsers = res.data.sort((a, b) => b.id - a.id);
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleFilter = (status) => {
    setFilterStatus(status);
    applyFilters(status, filterMobile);
  };

  const handleMobileFilter = (mobile) => {
    setFilterMobile(mobile);
    applyFilters(filterStatus, mobile);
  };

  const applyFilters = (status, mobile) => {
    let filtered = users;
    
    if (status === "pending") {
      filtered = filtered.filter(user => user.payment_status == 0);
    } else if (status === "paid") {
      filtered = filtered.filter(user => user.payment_status == 1);
    }
    
    if (mobile) {
      filtered = filtered.filter(user => user.mobile?.includes(mobile));
    }
    
    setFilteredUsers(filtered);
  };

  const handleReset = () => {
    setFilterStatus("all");
    setFilterMobile("");
    setFilteredUsers(users);
  };

  const handleShowDetails = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  // Approve Payment
  const handleApprove = async (id) => {
  setLoadingApprove(id);

  try {
    const res = await axios.put(
      `${baseUrl}/users/${id}/payment/enable`
    );

    // Remove approved user from list
    setUsers(users.filter(user => user.id !== id));

    // ✅ Get message properly
    const message = res.data?.message || "Payment updated";

    if (message.toLowerCase().includes("email sending failed")) {
      toast.warning(message);
    } else {
      toast.success(message);
    }

  } catch (error) {
    console.error("Approval failed:", error);

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      "Approval failed";

    toast.error(errorMessage);

  } finally {
    setLoadingApprove(null);
  }
};

  // Disapprove Payment
  const handleDisapprove = async (id) => {
    setSelectedUserId(id);
    setShowModal(true);
  };

  const submitDisapproval = () => {
  if (!reason.trim()) {
    toast.warning("Please enter a reason");
    return;
  }

  setLoadingDisapprove(true);

  axios.put(
    `${baseUrl}/user/${selectedUserId}/payment/disable`,
    null,
    { params: { reason } }
  )
  .then((res) => {
    setUsers(users.filter(user => user.id !== selectedUserId));
    setShowModal(false);
    setReason("");

    // 
    const message =
      res.data?.message ||
      res.data?.data ||
      "Payment updated";

    if (message.toLowerCase().includes("email sending failed")) {
      toast.warning(message);
    } else {
      toast.error(message);   // rejected case
    }
  })
  .catch((error) => {
    console.error("Disapproval failed:", error);

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      "Disapproval failed";

    toast.error(errorMessage);
  })
  .finally(() => {
    setLoadingDisapprove(false);
  });
};

  // Delete User
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    setLoadingDelete(id);
    try {
      await axios.delete(`${baseUrl}/users/${id}`);
      setUsers(users.filter(user => user.id !== id));
      setFilteredUsers(filteredUsers.filter(user => user.id !== id));
      toast.success("User deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Delete failed");
    } finally {
      setLoadingDelete(null);
    }
  };

  // Convert 24-hour time to 12-hour format
  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container-fluid p-3 p-md-4 d-flex justify-content-center">
        <div style={{ maxWidth: "1400px", width: "100%" }}>
      <h2 className="mb-4 text-center">Manage Payment Approvals</h2>

      <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
        <label className="fw-bold">Filter:</label>
        <select 
          className="form-select" 
          style={{ width: "200px" }}
          value={filterStatus}
          onChange={(e) => handleFilter(e.target.value)}
        >
          <option value="all">All Payments</option>
          <option value="pending">Pending Payments</option>
          <option value="paid">Paid Payments</option>
        </select>
        <input
          type="number"
          className="form-control"
          style={{ width: "200px" }}
          placeholder="Search by mobile"
          value={filterMobile}
          onChange={(e) => handleMobileFilter(e.target.value)}
        />
        <button 
          className="btn btn-secondary btn-sm"
          onClick={handleReset}
        >
          Reset
        </button>
      </div>

      <div className="mb-3 d-flex justify-content-end gap-3 align-items-center flex-wrap" style={{ fontSize: "14px" }}>
        <span><FaEye color="#28a745" /> View Details</span>
        <span><FaCheck color="#28a745" /> Approve</span>
        <span><FaTimes color="#ff9800" /> Disapprove</span>
        <span><FaTrash color="#dc3545" /> Delete</span>
      </div>

      <style>{`
        .orange-scrollbar::-webkit-scrollbar {
          width: 12px;
        }
        .orange-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .orange-scrollbar::-webkit-scrollbar-thumb {
          background: #ff9800;
          border-radius: 6px;
        }
        .orange-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #e68900;
        }
      `}</style>
      <div className="table-responsive orange-scrollbar" style={{ maxHeight: "600px", overflowY: "auto" }}>
        <table className="table table-bordered table-hover">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Mobile</th>
              <th>Transaction Type</th>
              <th>Transaction ID</th>
              <th>Date</th>
              <th>Time</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No Payments Found
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.mobile}</td>
                  <td>{user.transaction_type}</td>
                  <td>{user.transaction_id}</td>
                  <td>{user.transaction_date}</td>
                  <td>{formatTime(user.transaction_time)}</td>
                  <td>
                    <div className="d-flex gap-2 justify-content-center align-items-center">
                      <FaEye
                        size={20}
                        color="#28a745"
                        style={{ cursor: "pointer" }}
                        title="View Details"
                        onClick={() => handleShowDetails(user)}
                      />
                      {user.payment_status == 0 ? (
                        <>
                          {loadingApprove === user.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FaCheck
                              size={20}
                              color="#28a745"
                              style={{ cursor: "pointer" }}
                              title="Approve"
                              onClick={() => handleApprove(user.id)}
                            />
                          )}
                          <FaTimes
                            size={20}
                            color="#ff9800"
                            style={{ cursor: "pointer" }}
                            title="Disapprove"
                            onClick={() => handleDisapprove(user.id)}
                          />
                          {loadingDelete === user.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FaTrash
                              size={20}
                              color="#dc3545"
                              style={{ cursor: "pointer" }}
                              title="Delete"
                              onClick={() => handleDelete(user.id)}
                            />
                          )}
                        </>
                      ) : (
                        <span className="text-success fw-bold">Paid</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showDetailsModal && selectedUser && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">User Details</h5>
                <button 
                  className="btn-close" 
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>
              <div className="modal-body text-start">
                <div className="mb-3 text-start">
                  <img
                    src={`https://lingayatwelfarekarnataka.com/${selectedUser.profile_photo_path}`}
                    alt="Profile"
                    className="rounded-circle shadow"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      border: "3px solid #0d6efd",
                      display: "block"
                    }}
                  />
                </div>
                <div className="row text-start" style={{ rowGap: "15px" }}>
                  <div className="col-md-6">
                    <strong>Name:</strong> {selectedUser.name}
                  </div>
                  <div className="col-md-6">
                    <strong>Email:</strong> {selectedUser.email}
                  </div>
                  <div className="col-md-6">
                    <strong>Mobile:</strong> {selectedUser.mobile}
                  </div>
                  <div className="col-md-6">
                    <strong>Gender:</strong> {selectedUser.gender}
                  </div>
                  <div className="col-md-6">
                    <strong>Age:</strong> {selectedUser.age}
                  </div>
                  <div className="col-md-6">
                    <strong>Date of Birth:</strong> {selectedUser.date_of_birth}
                  </div>
                  <div className="col-md-6">
                    <strong>Birth Place:</strong> {selectedUser.birth_place}
                  </div>
                  <div className="col-md-6">
                    <strong>Birth Time:</strong> {selectedUser.birth_time}
                  </div>
                  <div className="col-md-6">
                    <strong>Height:</strong> {selectedUser.height} cm
                  </div>
                  <div className="col-md-6">
                    <strong>Weight:</strong> {selectedUser.weight} kg
                  </div>
                  <div className="col-md-6">
                    <strong>Raasi:</strong> {selectedUser.raasi}
                  </div>
                  <div className="col-md-6">
                    <strong>Star:</strong> {selectedUser.star}
                  </div>
                  <div className="col-md-6">
                    <strong>City:</strong> {selectedUser.city}
                  </div>
                  <div className="col-md-6">
                    <strong>Address:</strong> {selectedUser.address}
                  </div>
                  <div className="col-md-6">
                    <strong>Work Location:</strong> {selectedUser.work_location}
                  </div>
                  <div className="col-md-6">
                    <strong>Qualification:</strong> {selectedUser.qualification}
                  </div>
                  <div className="col-md-6">
                    <strong>Annual Income:</strong> {selectedUser.annual_income}
                  </div>
                  <div className="col-md-12">
                    <strong>About Me:</strong> {selectedUser.about_me}
                  </div>
                  <div className="col-md-6">
                    <strong>Transaction Type:</strong> {selectedUser.transaction_type}
                  </div>
                  <div className="col-md-6">
                    <strong>Transaction ID:</strong> {selectedUser.transaction_id}
                  </div>
                  <div className="col-md-6">
                    <strong>Transaction Date:</strong> {selectedUser.transaction_date}
                  </div>
                  <div className="col-md-6">
                    <strong>Transaction Time:</strong> {formatTime(selectedUser.transaction_time)}
                  </div>
                  <div className="col-md-6">
                    <strong>Payment Status:</strong> 
                    <span className={selectedUser.payment_status == 1 ? "text-success" : "text-warning"}>
                      {selectedUser.payment_status == 1 ? "Paid" : "Pending"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Disapproval Reason</h5>
              </div>
              <div className="modal-body">
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for disapproval"
                  className="form-control"
                  rows="4"
                />
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => { setShowModal(false); setReason(""); }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={submitDisapproval}
                  disabled={loadingDisapprove}
                  className="btn btn-danger d-flex align-items-center gap-1"
                >
                  {loadingDisapprove && <Spinner animation="border" size="sm" />}
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
    </div>
    </>
  );
}

export default ManageUsers;
